import {
  ConfirmationModal,
  getDefaultActiveTableFilterOptions,
  LoadingSpinner,
  Table,
  Tooltip,
  useAlert,
  OptionsIndicator,
  GET_ALL_FORMS_FOR_CURRENT_USER,
  GET_FORM_VERSIONS_BY_IDS,
  UPDATE_FORM,
  DELETE_FORM,
  GET_EMPLOYEES_FOR_CURRENT_COMPANY_BY_IDS,
  GET_EMPLOYEES_COUNT,
  GET_FORM_VERSIONS_COUNT,
  GET_TARGET_AUDIENCE_COUNTS,
  Button,
} from '@employ-engage/common-components';
import { useEffect, useState } from 'react';
import { byDate } from './form-dates';
import {
  ActiveTableFilterOption,
  ActiveTableToggle,
  BasicTableDataRow,
  Employee,
  FormType,
  FormVersion,
  TableCellContentAlignEnum,
  TableFilter,
  TableSortOption,
  TableToggle,
} from '@employ-engage/data';
import AddFormModal from '../add-form-modal/add-form-modal';
import { FormWithCompleteTargets } from '../add-form-form/add-form-form';
import DuplicateFormModal from '../duplicate-form-modal/duplicate-form-modal';
import classNames from 'classnames';
import { Trans, useTranslation } from 'react-i18next';
import { useApolloClient, useMutation, useQuery } from '@apollo/client';
import uniq from 'lodash.uniq';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  faPenToSquare,
  faCopy,
  faTrash,
  faArchive,
  faEnvelope,
  faPaperPlane,
  faUsers,
} from '@fortawesome/pro-solid-svg-icons';
import { Link } from 'react-router-dom';

/* eslint-disable-next-line */
export interface AdminFormsViewProps {}

export function AdminFormsView(props: AdminFormsViewProps) {
  const { t } = useTranslation(['common']);
  const [formVersions, setFormVersions] = useState<FormVersion[]>([]);
  const [isAddFormModalOpen, setIsAddFormModalOpen] = useState(false);
  const [formSelectedForEditing, setFormSelectedForEditing] =
    useState<FormWithCompleteTargets | null>(null);
  const [isDuplicateFormModalOpen, setIsDuplicateFormModalOpen] =
    useState(false);
  const [formSelectedForDuplication, setFormSelectedForDuplication] = useState<
    FormType | undefined
  >();
  const [openSendReminderEmailModal, setOpenSendReminderEmailModal] =
    useState(false);
  const [
    formSelectedForSendingReminderEmails,
    setFormSelectedForSendingReminderEmails,
  ] = useState<FormType | undefined>();
  const [
    openArchiveFormConfirmationModal,
    setOpenArchiveFormConfirmationModal,
  ] = useState(false);
  const [formSelectedForArchiving, setFormSelectedForArchiving] = useState<
    FormType | undefined
  >();
  const [openDeleteFormConfirmationModal, setOpenDeleteFormConfirmationModal] =
    useState(false);
  const [formSelectedForDeletion, setFormSelectedForDeletion] = useState<
    FormType | undefined
  >();
  const {
    data: { forms } = {},
    loading: isFormsLoading,
    refetch: refetchForms,
  } = useQuery(GET_ALL_FORMS_FOR_CURRENT_USER);
  const apolloClient = useApolloClient();

  const [updateForm] = useMutation(UPDATE_FORM);
  const [deleteFormAndResponsesWithId] = useMutation(DELETE_FORM);
  const { data: { employeesCount } = {} } = useQuery(GET_EMPLOYEES_COUNT, {
    fetchPolicy: 'network-only',
  });
  const { data: { formVersionsCount } = {} } = useQuery(
    GET_FORM_VERSIONS_COUNT,
    {
      fetchPolicy: 'network-only',
    }
  );

  const getTargetIndividualsForForm = async (
    form: FormType
  ): Promise<FormWithCompleteTargets> => {
    const employeIds = form.targets
      .map((target) =>
        target.targetIndividuals
          .concat(target.targetIndividualsForResponses)
          .concat(target.targetIndividualsForTextResponses)
      )
      .flat();

    const {
      data: { employeesByIds },
    } = await apolloClient.query({
      query: GET_EMPLOYEES_FOR_CURRENT_COMPANY_BY_IDS,
      variables: {
        ids: uniq(employeIds),
      },
    });

    // Someone archived since the form was saved comes back as nothing. They
    // are reached by no send either way, and an `undefined` in the list broke
    // everything downstream that reads `.id`.
    const toEmployees = (ids: string[]): Employee[] =>
      ids
        .map((id) => employeesByIds.find((e: any) => e.id === id))
        .filter(Boolean);

    return {
      ...form,
      targets: form.targets.map((target) => ({
        ...target,
        targetIndividuals: toEmployees(target.targetIndividuals),
        targetIndividualsForResponses: toEmployees(
          target.targetIndividualsForResponses
        ),
        targetIndividualsForTextResponses: toEmployees(
          target.targetIndividualsForTextResponses
        ),
      })),
    };
  };

  useEffect(() => {
    if (forms && forms.length > 0) {
      const ids = uniq(
        forms.map((form: FormType) => form.templateVersionId as string)
      );
      apolloClient
        .query({
          query: GET_FORM_VERSIONS_BY_IDS,
          variables: {
            ids,
          },
        })
        .then(({ data: { formVersionsByIds } }) => {
          setFormVersions(formVersionsByIds);
        });
    }
  }, [forms, apolloClient]);

  const { fireAlert: fireFormArchivedAlert, Alert: FormArchivedAlert } =
    useAlert({
      type: 'success',
      title: t('Success'),
      message: t('Form archived!'),
    });
  const { fireAlert: fireFormDeletedAlert, Alert: FormDeletedAlert } = useAlert(
    {
      type: 'success',
      title: t('Success'),
      message: t('Form deleted!'),
    }
  );
  const { fireAlert: fireFormEmailSentAlert, Alert: FormEmailSentAlert } =
    useAlert({
      type: 'success',
      title: t('Success'),
      message: t('Reminder emails sent!'),
    });

  const { fireAlert: fireEmailFailedAlert, Alert: EmailFailedAlert } =
    useAlert({
      type: 'error',
      title: t('Error'),
      message: t("The emails couldn't be sent."),
    });
  const {
    fireAlert: fireTestSentAlert,
    Alert: TestSentAlert,
    setAlertMessage: setTestSentMessage,
  } = useAlert({
    type: 'success',
    title: t('Success'),
    message: t('Test email is on its way to you.'),
  });

  /**
   * Who the reminder would reach, for the confirmation: every target, less
   * anyone who has completed the form. Only fetched while it is being asked.
   */
  const { data: reminderAudienceData } = useQuery(GET_TARGET_AUDIENCE_COUNTS, {
    skip: !openSendReminderEmailModal || !formSelectedForSendingReminderEmails,
    fetchPolicy: 'network-only',
    variables: {
      targets: (formSelectedForSendingReminderEmails?.targets ?? []).map(
        (target: any) => ({
          targetDepartments: target.targetDepartments ?? [],
          targetManagementRoles: (target.targetManagementRoles ?? []).map(
            (role: any) => ({ id: role.id, name: role.name })
          ),
          targetIndividuals: target.targetIndividuals ?? [],
          shouldSendConfirmationEmailToFormSubmitter: false,
          shouldSendConfirmationSmsToFormSubmitter: false,
        })
      ),
      emailTargetIndexes: (
        formSelectedForSendingReminderEmails?.targets ?? []
      ).map((_: unknown, index: number) => index),
      formId: formSelectedForSendingReminderEmails?.id,
    },
  });
  const reminderAudience = reminderAudienceData?.targetAudienceCounts;

  /**
   * The invitation as recipients would get it, to the signed-in admin only.
   * The server decides what the link may open; see `handleTestInviteEmail`.
   */
  const sendTestEmail = async (form: FormType) => {
    try {
      const response = await fetch(
        `${
          import.meta.env.VITE_API_ENDPOINT
        }/protected-api/enqueue-emails?type=test-invite`,
        {
          method: 'POST',
          credentials: 'include',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ formId: form.id }),
        }
      );
      const { jobIds = [] } = response.ok
        ? await response.json().catch(() => ({}))
        : {};
      if (jobIds.length === 0) {
        fireEmailFailedAlert(5000);
        return;
      }
      setTestSentMessage(
        t('Test email for "{{title}}" is on its way to you.', { title: form.title })
      );
      fireTestSentAlert(4000);
    } catch (error) {
      console.error('Error sending test email:', error);
      fireEmailFailedAlert(5000);
    }
  };

  const onClickSendReminderEmail = () => {
    setOpenSendReminderEmailModal(true);
  };

  const sendReminderEmails = (form: FormType | undefined) => {
    fetch(
      `${
        import.meta.env.VITE_API_ENDPOINT
      }/protected-api/enqueue-emails?type=reminder`,
      {
        method: 'POST',
        credentials: 'include',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          formId: form?.id,
          sendEmailsForTargets: Array(form?.targets.length).fill(true),
        }),
      }
    )
      .then((response) => {
        if (response.ok) {
          console.info('Reminder emails sent successfully');
          fireFormEmailSentAlert(3000);
        } else {
          console.error('Failed to send reminder emails');
          fireEmailFailedAlert(5000);
        }
      })
      .catch((error) => {
        console.error('Error sending reminder emails:', error);
        fireEmailFailedAlert(5000);
      });
  };

  const archiveForm = async (form: FormType | undefined) => {
    if (form) {
      await updateForm({
        variables: {
          id: form.id,
          input: {
            isArchived: true,
          },
        },
      });
      fireFormArchivedAlert(3000);
      refetchForms();
    }
  };

  const deleteForm = (form: FormType | undefined) => {
    if (form) {
      deleteFormAndResponsesWithId({
        variables: {
          formId: form.id,
        },
      }).then(() => {
        console.info('Form deleted successfully');
        fireFormDeletedAlert(3000);
        refetchForms();
      });
    }
  };

  const getFormVersion = (row: any) => {
    const formVersion = formVersions?.find((t: any) => {
      return t.id === row.templateVersionId;
    });

    if (formVersion) {
      return formVersion;
    }
    return {} as FormVersion;
  };

  const filters: TableFilter[] = [
    {
      id: 'options',
      value: t('Options'),
      options: [
        {
          key: '1',
          value: 'anonymous',
          label: t('Anonymous for All Respondents'),
          filter: (row) => {
            return getFormVersion(row)?.isAnonymous;
          },
        },
        {
          key: '2',
          value: 'infinite-responses',
          label: t('Infinite Responses Allowed'),
          filter: (row) => {
            return getFormVersion(row)?.isInfinitelyRespondable;
          },
        },
      ],
    },
  ] as TableFilter[];

  const toggles = [
    {
      id: 'show-archived',
      label: t('Show Archived'),
      filter: (_) => {
        return true;
      },
      negativeFilter: (row: any) => {
        return !row.isArchived;
      },
    },
  ] as TableToggle[];

  const [activeFilters, setActiveFilters] = useState<ActiveTableFilterOption[]>(
    getDefaultActiveTableFilterOptions(filters)
  );

  const [activeToggles, setActiveToggles] = useState<ActiveTableToggle[]>([]);

  const sortOptions: TableSortOption[] = [
    {
      value: 'Title (A-Z)',
      sort: (row1, row2) => {
        return row1.title.localeCompare(row2.title);
      },
    },
    {
      value: 'Title (Z-A)',
      sort: (row1, row2) => {
        return row2.title.localeCompare(row1.title);
      },
    },
    // These read `.seconds` off an ISO string, so every comparison was NaN
    // and nothing was sorted. A form with no date would now throw outright.
    { value: 'Open Date (Oldest)', sort: byDate('openDate', 'asc') },
    { value: 'Open Date (Newest)', sort: byDate('openDate', 'desc') },
    { value: 'Close Date (Oldest)', sort: byDate('closeDate', 'asc') },
    { value: 'Close Date (Newest)', sort: byDate('closeDate', 'desc') },
  ];

  const [activeSort, setActiveSort] = useState<TableSortOption>(sortOptions[0]);

  const onClickArchiveForm = (row: any) => {
    setFormSelectedForArchiving(row);
    setOpenArchiveFormConfirmationModal(true);
  };

  const onClickDeleteForm = (row: any) => {
    setFormSelectedForDeletion(row);
    setOpenDeleteFormConfirmationModal(true);
  };

  const mapData = (row: any, index: number) => {
    const templateVersion = getFormVersion(row);
    // `new Date(null)` is the epoch, not an error, so an absent date would
    // quietly render as 1/1/1970 rather than as absent.
    const openDate = row?.openDate
      ? t('dateShort', { date: new Date(row.openDate) })
      : '—';

    const closeDate = row?.closeDate
      ? t('dateShort', { date: new Date(row.closeDate) })
      : '—';
    return {
      isDeleted: row.isArchived,
      index,
      id: row.id,
      title: row.title,
      fromTemplateVersion: (
        <div>
          {templateVersion?.name}
          {templateVersion?.isAnonymous ? (
            <span className="text-light-secondaryAccent">
              {' '}
              {t('(Anonymous)')}
            </span>
          ) : (
            ''
          )}
        </div>
      ),
      numTargetsConfigured: row?.targets?.length || 0,
      openDate,
      closeDate,
      options: (
        <OptionsIndicator
          options={[
            {
              activeColor: 'text-orange-500',
              isActive: templateVersion.isAnonymous || false,
              icon: 'fa-question',
              label: t('Anonymous'),
            },
            {
              activeColor: 'text-purple-500',
              isActive: templateVersion.isInfinitelyRespondable || false,
              icon: 'fa-infinity',
              label: t('Infinite Responses'),
            },
            {
              activeColor: 'text-blue-500',
              isActive: templateVersion.isPasscodeRequired || false,
              icon: 'fa-key',
              label: t('Passcode Required'),
            },
            {
              activeColor: 'text-red-500',
              isActive: row?.addResponseEmailRecipients,
              icon: 'fa-mailbox',
              label: t('Response Emails'),
            },
            {
              activeColor: 'text-green-500',
              isActive: row?.addResponseTextRecipients,
              icon: 'fa-message-sms',
              label: t('Response SMS'),
            },
          ]}
        />
      ),
      actions: (
        <div className="flex justify-end space-x-1">
          <Tooltip label={t('Who Has Responded')}>
            <Link to={`${row.id}/responses`}>
              <FontAwesomeIcon
                icon={faUsers}
                className="text-light-primaryText dark:text-dark-primaryText hover:text-blue-500 hover:dark:text-blue-500"
              />
            </Link>
          </Tooltip>
          <Tooltip label={t('Edit Form')}>
            <button
              onClick={async () => {
                const newRow = await getTargetIndividualsForForm(row);
                setFormSelectedForEditing(newRow);
                setIsAddFormModalOpen(true);
              }}
            >
              <FontAwesomeIcon
                icon={faPenToSquare}
                className="text-light-primaryText dark:text-dark-primaryText hover:text-blue-500 hover:dark:text-blue-500"
              />
            </button>
          </Tooltip>
          <Tooltip label={t('Duplicate Form')}>
            <button
              onClick={() => {
                setFormSelectedForDuplication(row as unknown as FormType);
                setIsDuplicateFormModalOpen(true);
              }}
            >
              <FontAwesomeIcon
                icon={faCopy}
                className="text-light-primaryText dark:text-dark-primaryText hover:text-blue-500 hover:dark:text-blue-500"
              />
            </button>
          </Tooltip>
          <Tooltip label={t('Send a test to me')}>
            <button onClick={() => sendTestEmail(row as unknown as FormType)}>
              <FontAwesomeIcon
                icon={faPaperPlane}
                className="text-light-primaryText dark:text-dark-primaryText hover:text-blue-500 hover:dark:text-blue-500"
              />
            </button>
          </Tooltip>
          <Tooltip label={t('Email Reminder')}>
            <button
              onClick={() => {
                onClickSendReminderEmail();
                setFormSelectedForSendingReminderEmails({
                  ...(row as any),
                  isAnonymous: templateVersion?.isAnonymous,
                  isPasscodeRequired: templateVersion?.isPasscodeRequired,
                });
              }}
            >
              <FontAwesomeIcon
                icon={faEnvelope}
                className="text-light-primaryText dark:text-dark-primaryText hover:text-blue-500 hover:dark:text-blue-500"
              />
            </button>
          </Tooltip>
          <Tooltip
            label={row.isArchived ? t('Delete Form') : t('Archive Form')}
          >
            {row.isArchived ? (
              <button onClick={() => onClickDeleteForm(row)}>
                <FontAwesomeIcon
                  icon={faTrash}
                  className="text-light-primaryText dark:text-dark-primaryText hover:text-blue-500 hover:dark:text-blue-500"
                />
              </button>
            ) : (
              <button onClick={() => onClickArchiveForm(row)}>
                <FontAwesomeIcon
                  icon={faArchive}
                  className="text-light-primaryText dark:text-dark-primaryText hover:text-blue-500 hover:dark:text-blue-500"
                />
              </button>
            )}
          </Tooltip>
        </div>
      ),
    };
  };

  if (isFormsLoading) {
    return <LoadingSpinner spinnerId="admin-forms-view-loading" />;
  }

  return (
    <div className="space-y-7 flex flex-col">
      <div className="w-full pl-9 pr-[35.92px] pt-[37.91px] pb-[36.99px] bg-gradient-to-l from-light-primary to-light-primary rounded-lg justify-center items-center inline-flex">
        <div className="grow shrink basis-0 self-stretch justify-between items-center inline-flex">
          <div className="flex-col justify-start gap-[8.10px] inline-flex">
            <div className="text-white text-2xl font-bold">{t('New Form')}</div>
            <div className="text-gray-200 text-base font-normal">
              <Trans i18nKey="adminSurveysViewNewFormDescription">
                Create a new form to collect information from your employees.
                <span className="font-bold">
                  NOTE: Forms are built from templates that have been published.
                </span>
                You will need to have created some templates first.
              </Trans>
            </div>
          </div>
        </div>
      </div>
      <Table
        label={t('Forms')}
        sortOptions={sortOptions.map((option) => ({
          ...option,
          value: t(option.value),
        }))}
        activeSort={{ ...activeSort, value: t(activeSort.value) }}
        setActiveSort={setActiveSort}
        filters={filters}
        activeFilters={activeFilters}
        setActiveFilters={setActiveFilters}
        toggles={toggles}
        activeToggles={activeToggles}
        setActiveToggles={setActiveToggles}
        headerList={[
          {
            id: 'title',
            headerLabel: t('Title'),
          },
          {
            id: 'fromTemplateVersion',
            headerLabel: t('From Template Version'),
          },
          {
            id: 'numTargetsConfigured',
            headerLabel: t('Number of Target Groups Configured'),
            alignment: TableCellContentAlignEnum.CENTER,
          },
          {
            id: 'openDate',
            headerLabel: t('Open Date'),
            alignment: TableCellContentAlignEnum.CENTER,
          },
          {
            id: 'closeDate',
            headerLabel: t('Close Date'),
            alignment: TableCellContentAlignEnum.CENTER,
          },
          {
            id: 'options',
            headerLabel: t('Options'),
          },
          {
            id: 'actions',
            headerLabel: t('Actions'),
            alignment: TableCellContentAlignEnum.CENTER,
          },
        ]}
        actionItems={
          <div className="h-[49px] justify-end items-center gap-3.5 inline-flex">
            <Tooltip
              label={
                formVersionsCount?.count === 0 || employeesCount?.count === 0
                  ? t(
                      'Adding forms requires at least one active employee and at least one published template'
                    )
                  : t('Add Form')
              }
            >
              <div>
                <Button
                  onClick={() => {
                    setFormSelectedForEditing(null);
                    setIsAddFormModalOpen(true);
                  }}
                  variant="primary"
                  size="large"
                  className="p-[14.53px] bg-light-primary rounded-2xl justify-start items-center gap-[7.27px] flex"
                  disabled={
                    formVersionsCount?.count === 0 ||
                    employeesCount?.count === 0
                  }
                >
                  <i className="fa-solid fa-plus text-light-primaryBackground dark:text-custom-textColor text-md" />
                </Button>
              </div>
            </Tooltip>
          </div>
        }
        dataList={(forms as unknown as BasicTableDataRow[]) || []}
        mapData={mapData}
      />
      <AddFormModal
        open={isAddFormModalOpen}
        setOpen={setIsAddFormModalOpen}
        formSelectedForEditing={formSelectedForEditing}
        refetchForms={refetchForms}
      />
      <DuplicateFormModal
        open={isDuplicateFormModalOpen}
        setOpen={setIsDuplicateFormModalOpen}
        formSelectedForDuplication={formSelectedForDuplication}
        refetchForms={refetchForms}
      />
      <ConfirmationModal
        text={
          reminderAudience
            ? t(
                'Send a reminder to {{count}} people who have not yet submitted their response? Emails will be sent immediately on confirmation.',
                { count: reminderAudience.emailTotal }
              )
            : t(
                'Are you sure you want to send a reminder email to all of the target recipients for this form who have not yet submitted their response? Emails will be sent immediately on confirmation.'
              )
        }
        warningText={
          [
            formSelectedForSendingReminderEmails?.isAnonymous
              ? t(
                  'NOTE: This is an anonymous form. Therefore all targeted individuals will receive a reminder regardless of whether they have already responded.'
                )
              : '',
            reminderAudience?.emailUnreachable
              ? t(
                  '{{count}} of them have an address that is missing, invalid, a test address, or has bounced before.',
                  { count: reminderAudience.emailUnreachable }
                )
              : '',
          ]
            .filter(Boolean)
            .join(' ')
        }
        open={openSendReminderEmailModal}
        setOpen={setOpenSendReminderEmailModal}
        onConfirm={() => {
          sendReminderEmails(formSelectedForSendingReminderEmails);
          setOpenSendReminderEmailModal(false);
        }}
      />
      <ConfirmationModal
        text={t(`archive-form-confirmation`, {
          title: formSelectedForArchiving?.title,
        })}
        open={openArchiveFormConfirmationModal}
        setOpen={setOpenArchiveFormConfirmationModal}
        onConfirm={() => {
          archiveForm(formSelectedForArchiving);
          setOpenSendReminderEmailModal(false);
        }}
      />
      <ConfirmationModal
        text={t(`delete-form-confirmation`, {
          title: formSelectedForDeletion?.title,
        })}
        warningText={t('deletion-warning-text')}
        open={openDeleteFormConfirmationModal}
        setOpen={setOpenDeleteFormConfirmationModal}
        onConfirm={() => {
          deleteForm(formSelectedForDeletion);
        }}
        confirmButtonText={t('Delete')}
        confirmButtonColorClasses={classNames(
          'bg-red-500',
          'focus:ring-red-500'
        )}
      />
      <FormArchivedAlert />
      <FormDeletedAlert />
      <FormEmailSentAlert />
      <EmailFailedAlert />
      <TestSentAlert />
    </div>
  );
}

export default AdminFormsView;
